<script lang="ts">
    import { mdiChevronDown, mdiChevronRight } from "@mdi/js"
    import SubNavEl from "./SubNavEl.svelte"
    import Icon from "../../../widgets/Icon.svelte"
    import { spaLink, spaNavigate } from "../../../../functionTools/navigation"
    import MediaQuery from "../../../widgets/MediaQuery.svelte"
    import MobileAdditionals from "./MobileAdditionals.svelte"
    import { _ } from "svelte-i18n"

    let {
        navEl,
        closeMenu,
        iconSnippet,
        classList,
    }: { navEl: NavigationNode; classList?: string; closeMenu: () => void; iconSnippet?: () => any } = $props()

    // Einträge mit leftMenuOnly werden nur in der linken Navigation gezeigt,
    // nicht im Mega-Menü (Desktop) und mobilen Menü.
    let subNodes = $derived((navEl.nodes || []).filter((node) => !node.leftMenuOnly))

    let hoverTimer: ReturnType<typeof setTimeout> | null = null
    let offHoverTimer: ReturnType<typeof setTimeout> | null = null
    let isHovered: boolean = $state(false)

    function handleMouseEnter() {
        clearTimeout(offHoverTimer)
        hoverTimer = setTimeout(() => {
            isHovered = true
        }, 0)
    }

    function handleMouseLeave() {
        clearTimeout(hoverTimer)
        offHoverTimer = setTimeout(() => {
            isHovered = false
        }, 200)
    }
    function forceClose() {
        clearTimeout(hoverTimer)
        clearTimeout(offHoverTimer)
        isHovered = false
    }
    let mobileSubnavOpen = $state(false)
    let activeMobileSubnavPath: string | null = $state(null)

    $effect(() => {
        if (!mobileSubnavOpen) activeMobileSubnavPath = null
    })
</script>

<li
    class="nav-element {classList}"
    onmouseenter={handleMouseEnter}
    onmouseleave={handleMouseLeave}
    ontouchstart={handleMouseEnter}
    ontouchend={handleMouseLeave}
    onfocusin={handleMouseEnter}
    onfocusout={handleMouseLeave}
>
    <MediaQuery query={"(min-width: 1600px)"}>
        {#snippet contentSnippet()}
            <div class="parent-nav main">
                {#if navEl.external}
                    <a
                        href={navEl.externalUrl}
                        target={navEl.target || "_self"}
                        rel={navEl.target === "_blank" ? "noopener noreferrer" : undefined}
                        aria-haspopup="true"
                        aria-expanded={isHovered ? "true" : "false"}
                        onclick={(e) => {
                            e.preventDefault()
                            forceClose()
                        }}
                    >
                        {navEl.name}
                    </a>
                {:else}
                    <a
                        href={navEl.directUrl ? navEl.page : navEl.path}
                        use:spaLink
                        aria-haspopup="true"
                        aria-expanded={isHovered ? "true" : "false"}
                        onclick={(e) => {
                            e.preventDefault()
                            forceClose()
                        }}
                    >
                        {navEl.name}
                    </a>
                {/if}
                {#if subNodes.length > 0}
                    <Icon path={mdiChevronDown} size="1.2rem" />
                {/if}
            </div>
        {/snippet}
        {#snippet altSnippet()}
            <button
                class="parent-nav button transparent"
                role={navEl.directUrl || subNodes.length ? "link" : "button"}
                aria-haspopup="true"
                aria-expanded={isHovered ? "true" : "false"}
                onclick={(e) => {
                    if (navEl.external) {
                        closeMenu()
                        mobileSubnavOpen = false
                        if (navEl.target === "_blank") {
                            window.open(navEl.externalUrl, "_blank")
                        } else {
                            window.location.href = navEl.externalUrl
                        }
                    } else if (navEl.directUrl) {
                        closeMenu()
                        spaNavigate(navEl.page)
                        mobileSubnavOpen = false
                    } else if (subNodes.length === 0) {
                        mobileSubnavOpen = false
                        closeMenu()
                        spaNavigate(navEl.path || navEl.page || "/")
                    } else mobileSubnavOpen = !mobileSubnavOpen
                }}
            >
                <span style="display: flex; align-items: center; gap: 12px; ">
                    {#if iconSnippet}
                        {@render iconSnippet()}
                    {/if}
                    {navEl.name}
                </span>
                {#if subNodes.length > 0}
                    <Icon path={mdiChevronRight} size="1.2rem" />
                {/if}
            </button>
        {/snippet}
    </MediaQuery>
    {#if subNodes.length > 0}
        <section class="sub-nav {isHovered ? 'active' : ''}" class:mobileSubnavOpen id={"subpages-" + navEl.page}>
            <ul class="sub-nav-wrapper" class:childSubnavOpen={!!activeMobileSubnavPath}>
                <MobileAdditionals bind:mobileSubnavOpen path={navEl.path} title={navEl.name} {closeMenu} />

                {#each subNodes as subNavEl}
                    <SubNavEl {subNavEl} {closeMenu} {forceClose} bind:activeMobileSubnavPath />
                {/each}
            </ul>
        </section>
    {/if}
</li>

<style lang="less">
    @import "../../../../css/variables.less";
    .nav-element {
        .parent-nav.main {
            display: flex;
            justify-content: center;
            align-items: center;
            gap: 0.3rem;
            transition: color 200ms ease-in-out;
            cursor: pointer;

            a {
                color: var(--grey-400);
                text-decoration: none;
                cursor: pointer;
            }
            &:hover {
                color: var(--red-200);
                a {
                    color: var(--red-200);
                }
            }
        }

        .sub-nav {
            display: flex;
            position: absolute;
            top: -150vh;
            left: 0px;
            width: 100%;
            background-color: white;
            // transition: top 0.5s ease;
            justify-content: center;
            border-top: 1px solid var(--grey-200);
            box-shadow: 0px 10px 10px 0px rgba(0, 0, 0, 0.25);
            padding: 0px 1.2rem;

            .sub-nav-wrapper {
                display: flex;
                flex-wrap: wrap;
                justify-content: flex-start;
                width: 100%;
                padding: 2.4rem 0px;
                max-width: var(--max-container-width);
                gap: 2.4rem;
            }
        }

        cursor: pointer;

        .sub-nav.active {
            top: 100%;
        }

        @media @header_mobile {
            width: 100%;
            .sub-nav {
                top: 0%;
                left: -100vw;
                width: 100%;
                // transition: left 0.5s ease;
                justify-content: center;
                z-index: 100;
                &.active {
                    top: 0%;
                }
                padding: 0px;
                .sub-nav-wrapper {
                    display: flex;
                    flex-direction: column;
                    flex-wrap: nowrap;
                    justify-content: flex-start;
                    width: 100%;
                    padding: 12px 0px;
                    max-width: var(--max-container-width);
                    gap: 0rem;

                    &.childSubnavOpen > :global(li:not(.active-mobile-subnav)) {
                        display: none;
                    }
                }
                &.mobileSubnavOpen {
                    left: 0px;
                }
            }
        }
    }
</style>
